<template>
  <LoginSingleLayout wide>
    <Alert :i18nData="asyncError" />
    <Spinner :status="loading" />
    <CodeInputForm
      v-show="!loading"
      :subtitle="subtitle"
      :heading="$t('pages.twoFactor.heading')"
      :hint="hint"
      :submit-label="$t('pages.twoFactor.buttonOk')"
      :loading="loading"
      ref="codeForm"
      @submit="submitPin"
      @input="clearError"
    >
      <template #secondary>
        <button type="button" :disabled="resendDisabled" @click="onResend">
          {{ resend }}
        </button>
        <!-- aria-live="off": a per-second countdown announced by a screen reader
             would drown out the page. The disabled button already carries the state. -->
        <p
          v-if="resendDisabled"
          class="resend-countdown body-xs"
          role="timer"
          aria-live="off"
        >
          {{ countdownLabel }}
        </p>
      </template>
    </CodeInputForm>
  </LoginSingleLayout>
</template>

<script>
import { handlePostLoginResult } from '@/utils/login'
import { logError } from '@/utils/error'
import LoginSingleLayout from '@/components/LoginSingleLayout.vue'
import CodeInputForm from '@/components/CodeInputForm.vue'
import { mapState } from 'vuex'
import { TYPE_EMAIL, TWOFACTOR_RESEND_COOLDOWN_MS, E_SERVER_HTTP, E_CODE_UNAUTHORIZED, SUPPORT_EMAIL_ADDR } from '@/constants'
import { getFingerprintData } from '@/utils/fingerprint'
import nonceRefresh from '@/mixins/nonceRefresh'

export default {
  name: 'TwoFactorValidation',
  mixins: [nonceRefresh],
  components: {
    LoginSingleLayout,
    CodeInputForm
  },
  data () {
    return {
      loading: false,
      asyncError: null,
      fingerprint: {},
      // Deadline-based countdown: `now` is refreshed by the interval and the
      // remaining time is derived from it, so a throttled background tab can
      // skip ticks without the displayed value drifting.
      resendAvailableAt: 0,
      now: Date.now(),
      countdownId: null
    }
  },
  computed: {
    ...mapState(['twoFactorProxy']),
    isEmailProxy () {
      return this.twoFactorProxy && this.twoFactorProxy.type === TYPE_EMAIL
    },
    proxyValue () {
      return this.twoFactorProxy ? this.twoFactorProxy.value : ''
    },
    subtitle () {
      const key = this.isEmailProxy ? 'pages.twoFactor.subtitleEmail' : 'pages.twoFactor.subtitlePhone'
      return this.$t(key, { value: this.proxyValue })
    },
    hint () {
      return this.$t(this.isEmailProxy ? 'pages.twoFactor.hintEmail' : 'pages.twoFactor.hintPhone')
    },
    resend () {
      return this.$t('pages.twoFactor.resend')
    },
    secondsLeft () {
      return Math.max(0, Math.ceil((this.resendAvailableAt - this.now) / 1000))
    },
    resendDisabled () {
      return this.secondsLeft > 0
    },
    countdownLabel () {
      const time = `${Math.floor(this.secondsLeft / 60)}:${String(this.secondsLeft % 60).padStart(2, '0')}`
      return this.$t('pages.twoFactor.resendCountdown', { time })
    }
  },
  methods: {
    startResendCountdown () {
      this.resendAvailableAt = Date.now() + TWOFACTOR_RESEND_COOLDOWN_MS
      this.now = Date.now()
      if (this.countdownId) return
      // tick twice a second so the displayed second never lags behind real time
      this.countdownId = setInterval(() => {
        this.now = Date.now()
        if (this.now >= this.resendAvailableAt) {
          this.stopResendCountdown()
        }
      }, 500)
    },
    stopResendCountdown () {
      clearInterval(this.countdownId)
      this.countdownId = null
      this.now = Date.now()
    },
    async onResend () {
      // redundant with :disabled, but guards against a programmatic click
      if (this.resendDisabled) return
      this.clearError()
      try {
        await this.$store.dispatch('resend2FAPin')
        // the old code is dead now — don't leave its digits on screen
        this.$refs.codeForm.clear()
        this.startResendCountdown()
      } catch (err) {
        this.asyncError = err?.key ? { key: err.key, values: err.values } : { key: 'errors.unexpected' }
      }
    },
    clearError () {
      this.asyncError = null
    },
    async submitPin (pin) {
      this.loading = true
      try {
        const res = await this.$store.dispatch('validateTwoFactorPin', { code: pin, fingerprint: this.fingerprint })
        try {
          await handlePostLoginResult(res)
        } catch (err) {
          this.asyncError = err?.key ? { key: err.key, values: err.values } : { key: 'errors.unexpected' }
        }
      } catch (err) {
        this.loading = false
        if (err.id === E_SERVER_HTTP && err.code === E_CODE_UNAUTHORIZED) {
          this.asyncError = { key: 'pages.twoFactor.errorInvalidCode' }
        } else {
          this.asyncError = err?.key ? { key: err.key, values: err.values } : { key: 'errors.nonceExpired', values: { supportEmail: SUPPORT_EMAIL_ADDR } }
        }
        logError(err)
      }
    }
  },
  async created () {
    this.fingerprint = await getFingerprintData()
  },
  async mounted () {
    // the code was just sent by the login step, so the cooldown starts here
    this.startResendCountdown()
  },
  beforeDestroy () {
    this.stopResendCountdown()
  }
}
</script>

<style lang="scss" scoped>
.resend-countdown {
  margin: 0;
  // Grey/Grey 700 — same muted tone as the hint in CodeInputForm
  color: #626262;
}
</style>
